import { Grid } from "@mui/material";
import { ExternalUrls } from "../../../../lib/constants";
import { PricingContent } from "../../../../lib/pricing-content";
import { ProofBarData } from "../../../../lib/social-proof";
import { getFAQs } from "../../../../lib/faqs";
import { useFAQSchema, useProductSchema, ProductOffer } from "../../../../lib/seo";
import { useState } from "react";
import { PricingTiers } from "../../../../lib/pricing-data";
import { formatPrice, roundPrice } from "../../../../lib/pricing-utils";
import { useCurrency } from "../../../../contexts/CurrencyContext";

import { Section } from "../../../ui/Section";
import { SectionHeader } from "../../../ui/SectionHeader";
import { FadeIn } from "../../../ui/FadeIn";
import { PricingCard } from "../../../ui/PricingCard";
import { PricingTabHeader } from "../PricingTabHeader";
import { PricingAddonStrip } from "../PricingAddonStrip";
import { PricingSocialProofSection } from "../../PricingSocialProofSection";
import { FAQSection } from "../../FAQSection";
import { CallToActionCard } from "../../../ui/CallToActionCard";
import { PricingContentLayout } from "../../PricingContentLayout";

export function PricingCaseManagerView() {
    const { currency } = useCurrency();
    const [billingPeriod, setBillingPeriod] = useState<"monthly" | "annual">("monthly");
    const content = PricingContent.caseManager;
    const faqs = getFAQs('pricing-casemanager');
    useFAQSchema(faqs);
    const isAnnual = billingPeriod === "annual";

    // Product schema for SEO
    const productOffers: ProductOffer[] = [
        {
            name: 'Neumind Active (Client Self-Pay)',
            description: 'Empowering independence for settled or light-touch cases.',
            price: PricingTiers.professional.tierSingle.price[currency],
            priceCurrency: currency,
            availability: 'InStock',
            url: '/pricing'
        },
        {
            name: 'Recovery Arc Bundle',
            description: 'The complete digital ecosystem for complex rehabilitation with unlimited clinician seats.',
            price: PricingTiers.recoveryArc.price[currency],
            priceCurrency: currency,
            availability: 'InStock',
            url: '/pricing'
        }
    ];
    useProductSchema(productOffers);

    // Clone and modify strategy based on toggle
    /*
    const strategy = { ...content.strategy };
    // Create a new cardData object to avoid mutating the original reference permanently in this render cycle (spread is shallow)
    if (strategy.cardData) {
        strategy.cardData = { ...strategy.cardData };
        const priceVal = PricingTiers.recoveryArc.price[currency];

        if (isAnnual) {
            strategy.cardData.price = formatPrice(priceVal, currency);
            strategy.cardData.unit = '/yr';
            strategy.cardData.subtitle = 'per client (One-off payment)';
        } else {
            // Show broken down monthly cost
            strategy.cardData.price = formatPrice(roundPrice(priceVal / 12), currency);
            strategy.cardData.unit = '/mo';
            strategy.cardData.subtitle = `Billed annually as ${formatPrice(priceVal, currency)}`;
        }
    }
    */

    return (
        <>
            <Section centered py={6} maxWidth="xl">
                <PricingTabHeader
                    title={content.header.title}
                    description={content.header.description}
                    proofBarItems={ProofBarData.complete}
                    textMb={2}
                />

                {/* Global Billing Toggle removed here, moved to layout */}

                <PricingContentLayout
                    maxWidth={10}
                    billingPeriod={billingPeriod}
                    onBillingPeriodChange={setBillingPeriod}
                >
                    {/* LEFT: Neumind Active (Client Self-Pay) */}
                    <Grid item xs={12} md={5}>
                        <FadeIn delay={100} direction="up" style={{ height: '100%' }}>
                            <PricingCard
                                title="Neumind Active (Client Self-Pay)"
                                caption="Empowering independence for settled or light-touch cases."
                                price={formatPrice(PricingTiers.professional.tierSingle.price[currency], currency)}
                                priceSuffix="/ month"
                                priceNote="Paid directly by Client or via Deputy Card"
                                description="**Ideal For:** Clients managing their own rehabilitation budget post-settlement. Long-term maintenance monitoring with lighter clinical oversight."
                                features={[
                                    {
                                        text: "<b>Clinician Dashboard Link:</b> Seamlessly connects to your professional account for remote monitoring.",
                                        tooltip: "Clinicians can view client progress graphs and activity logs in real-time."
                                    },
                                    "<b>Standard Reporting:</b> Access to in-app analytics for fatigue, mood, and memory trends.",
                                    "<b>Care Circle Access:</b> The client can invite unlimited family members or carers.",
                                    "<b>'Draft Now, Pay Later':</b> You configure the clinical plan in draft mode."
                                ]}
                                ctaText="Create Free Account"
                                ctaHref={ExternalUrls.dashboardSignup}
                                ctaSubText="No credit card required"
                                highlighted={false}
                            />
                        </FadeIn>
                    </Grid>

                    {/* RIGHT: The Recovery Arc Bundle */}
                    <Grid item xs={12} md={5}>
                        <FadeIn delay={200} direction="up" style={{ height: '100%' }}>
                            <PricingCard
                                overhead="Target Audience: Case Management Firms"
                                title="Recovery Arc Bundle"
                                caption="The complete digital ecosystem for complex rehabilitation."
                                highlighted={true}
                                highlightText="Total Care"
                                price={
                                    isAnnual
                                        ? formatPrice(PricingTiers.recoveryArc.price[currency], currency)
                                        : formatPrice(roundPrice(PricingTiers.recoveryArc.price[currency] / 12), currency)
                                }
                                priceSuffix={isAnnual ? "/ client / year" : "/ client / month"}
                                priceNote={
                                    isAnnual
                                        ? "Includes unlimited clinician seats"
                                        : `Billed annually as ${formatPrice(PricingTiers.recoveryArc.price[currency], currency)}`
                                }
                                description="**Strategic Goal:** Up-sell the value of data, security, and team collaboration."
                                features={[
                                    {
                                        text: "<b>Defensible Data Reporting:</b> Quarterly outcome reports visualizing longitudinal trends.",
                                        tooltip: "Objective charts showing engagement, mood, and strategy usage over time for court evidence."
                                    },
                                    "<b>Universal Team Access:</b> Eliminate silos with unlimited licenses for the MDT.",
                                    "<b>Risk Mitigation & Sovereignty:</b> Enterprise-grade, GDPR-compliant data storage.",
                                    "<b>Family & Support Circle:</b> Includes free access for up to 5 family members."
                                ]}
                                ctaText="Book free consultation"
                                ctaHref={ExternalUrls.calendly}
                                ctaVariant="contained"
                            />
                        </FadeIn>
                    </Grid>

                    {/*
                    LEGACY COPY: CaseManager Strategy (Preserved for Reference)
                    <Grid item xs={12} md={8}>
                        <FadeIn delay={200} direction="left" style={{ height: '100%' }}>
                            <PayerStrategyContent
                                strategyId={99}
                                content={strategy}
                                style="content"
                            />
                        </FadeIn>
                    </Grid>
                    */}
                </PricingContentLayout>

            </Section>

            {/* Implementation Add-Ons */}
            <Section variant="muted" py={8}>
                <FadeIn direction="up">
                    <SectionHeader
                        overline="Optional Add-ons"
                        title="Enhance your implementation"
                        subtitle="Optional add-ons to help you get the most out of Neumind."
                        centered
                        titleVariant="h2"
                    />
                </FadeIn>
                <PricingAddonStrip data={content.addOns} />
            </Section>

            {/* Stats Section 3 (Case Management) */}
            <Section variant="dark" py={8}>
                <SectionHeader
                    title="Evidence-based rehabilitation"
                    subtitle="Data-driven insights for complex cases and funding requests."
                    variant="dark"
                    centered
                    sx={{ mb: 8 }}
                />
                <PricingSocialProofSection
                    userType={['professional']}
                    metricIds={['independence', 'timeSaving']}
                    bgVariant="dark"
                />
            </Section>

            {/* Common Questions */}
            <Section variant="muted" py={8}>
                <SectionHeader
                    title="Common Questions"
                    centered
                    sx={{ mb: 8 }}
                />
                <FAQSection faqs={faqs} />
            </Section>

            {/* CTA */}
            <Section py={12}>
                <CallToActionCard
                    location="pricing_case_managers_bottom_cta"
                    title="Ready to streamline your caseload?"
                    description="Discover how Neumind helps case managers deliver better outcomes with less admin."
                    primaryCTA={{ label: "Book Free Consultation", href: ExternalUrls.calendly, external: true }}
                    secondaryCTA={{ label: "Contact Us", href: "/contact-us" }}
                />
            </Section>
        </>
    );
}
